import React, { useState, useEffect } from "react";
import Breadcrumbs from "../../core/breadcrumbs";
import { User } from "react-feather";
import Select from "react-select";
import DateRangePicker from "react-bootstrap-daterangepicker";
import moment from "moment";
import { getAllInvoiceReport, downloadInvoicePDF, downloadInvoiceExcel } from "../../service/ApiServices";
import { Download } from "feather-icons-react/build/IconComponents";
import { Pagination } from "antd";

const Invoicereport = () => {

  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState({ pdf: false, excel: false });
  const [invoiceData, setInvoiceData] = useState({
    data: [],
    total: 0,
    totalAmount: 0,
  });

  const [params, setParams] = useState({
    page: 1,
    limit: 10,
  });

  const [filters, setFilters] = useState({
    search: "",
    customerName: "",
    status: "all",
    startDate: "",
    endDate: "",
  });

  // -------------------- STATUS OPTIONS --------------------
  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "paid", label: "Paid" },
    { value: "not-paid", label: "Not Paid" },
    { value: "partially-paid", label: "Partially Paid" },
    { value: "cancelled", label: "Cancelled" },
  ];

  // -------------------- FETCH DATA --------------------
  const fetchInvoices = async (page) => {
    try {
      console.log("Filters", filters)
      setLoading(true);

      const res = await getAllInvoiceReport({
        page: page ? page : params.page,
        limit: params.limit,
        invoiceNumber: filters.search,
        customerName: filters.customerName,
        status: filters.status !== "all" ? filters.status : undefined,
        startDate: filters.startDate,
        endDate: filters.endDate
      });

      setInvoiceData((prev) => ({
        ...prev,
        data: res?.data || [],
        total: res?.total || 0,
        totalAmount: res?.totalAmount || 0
      }));

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  // -------------------- EFFECT --------------------
  useEffect(() => {
    const delay = setTimeout(() => {
      fetchInvoices();
    }, 400); // debounce

    return () => clearTimeout(delay);
  }, [params]);

  // -------------------- HANDLERS --------------------
  const handleFilterChange = (key, value) => {
    setInvoiceData((prev) => ({ ...prev, page: 1 })); // reset page
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleDateRange = (event, picker) => {
    handleFilterChange(
      "startDate",
      picker.startDate.startOf("day").toISOString()
    );
    handleFilterChange(
      "endDate",
      picker.endDate.endOf("day").toISOString()
    );
  };

  const handleRefresh = () => {
    setFilters({
      search: "",
      customerName: "",
      status: "all",
      startDate: "",
      endDate: "",
    });
  };

  // -------------------- TOTALS --------------------
  const pageTotal = invoiceData.data.reduce(
    (sum, i) => sum + (i.total_after_discount || 0),
    0
  );

  const handleDownloadExcel = async () => {
    try {
      setDownloading(prev => ({ ...prev, excel: true }));
      await downloadInvoiceExcel({
        invoiceNumber: filters.search,
        customerName: filters.customerName,
        status: filters.status !== "all" ? filters.status : undefined,
        startDate: filters.startDate,
        endDate: filters.endDate,
      });
    } catch (err) {
      console.error("Excel download failed", err);
    } finally {
      setDownloading(prev => ({ ...prev, excel: false }));
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setDownloading(prev => ({ ...prev, pdf: true }));
      await downloadInvoicePDF({
        invoiceNumber: filters.search,
        customerName: filters.customerName,
        status: filters.status !== "all" ? filters.status : undefined,
        startDate: filters.startDate,
        endDate: filters.endDate,
      });
    } catch (err) {
      console.error("PDF download failed", err);
    } finally {
      setDownloading(prev => ({ ...prev, pdf: false }));
    }
  };
  // -------------------- UI --------------------
  return (
    <div className="page-wrapper">
      <div className="content">
        <Breadcrumbs
          maintitle="Invoice Report"
          subtitle="Manage Your Invoice Report"
          onRefresh={handleRefresh}
          downloadPDF={handleDownloadPDF}
          downloadExcel={handleDownloadExcel}
          downloading={downloading}

        />

        <div className="card">
          <div className="card-body">

            {/* 🔍 TOP BAR */}
            <div className="d-flex row  mb-3">


              <div className="col-md-3 mb-2">
                <div className="input-group">
                  <span className="input-group-text"><User size={14} /></span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Customer Name"
                    value={filters.customerName}
                    onChange={(e) =>
                      handleFilterChange("customerName", e.target.value)
                    }
                  />
                </div>
              </div>
              <div className="col-md-2 mb-2">
                <Select
                  options={statusOptions}
                  value={statusOptions.find(
                    (s) => s.value === filters.status
                  )}
                  onChange={(opt) =>
                    handleFilterChange("status", opt?.value || "all")
                  }
                />
              </div>

              <div className="col-md-4 mb-2">
                <DateRangePicker onApply={handleDateRange}>
                  <input
                    className="form-control"
                    placeholder={'Select Dates'}
                    value={
                      filters.startDate && filters.endDate
                        ? `${moment(filters.startDate).format("YYYY-MM-DD")} → ${moment(filters.endDate).format("YYYY-MM-DD")}`
                        : ""
                    }
                    readOnly
                  />
                </DateRangePicker>
              </div>
              <div className="col-md-2 mb-2">
                <div className="page-btn">
                  <a
                    className="btn btn-added btn-warning rounded"
                    onClick={() => {
                      fetchInvoices(1)
                    }}
                  >
                    <Download className="me-2" />
                    Get Data
                  </a>
                </div>
              </div>

            </div>



            {/* 📊 TABLE */}
            <div className="table-responsive">
              <table className="table table-striped">

                <thead>
                  <tr>
                    <th>Invoice No</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Due Date</th>
                    <th>Total</th>
                    <th>Paid</th>
                    <th>Due</th>
                    <th>Status</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : invoiceData.data.length > 0 ? (
                    invoiceData.data.map((inv) => (
                      <tr key={inv._id}>
                        <td>{inv.invoice_number}</td>
                        <td>{moment(inv.invoice_date).format("YYYY-MM-DD")}</td>
                        <td>{inv.customer?.customerName}</td>
                        <td>
                          {inv.due_date
                            ? moment(inv.due_date).format("YYYY-MM-DD")
                            : "-"}
                        </td>
                        <td>{(inv.total_after_discount || 0).toFixed(2)}</td>
                        <td>{(inv.paidAmount || 0).toFixed(2)}</td>
                        <td>
                          {(
                            (inv.total_after_discount || 0) -
                            (inv.paidAmount || 0)
                          ).toFixed(2)}
                        </td>
                        <td>
                          <span className={`badge ${inv.status === "paid"
                            ? "bg-success"
                            : inv.status === "not-paid"
                              ? "bg-danger"
                              : "bg-warning"
                            }`}>
                            {inv.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center">
                        No data found
                      </td>
                    </tr>
                  )}
                </tbody>

                {/* 🔥 FOOTER TOTALS */}
                <tfoot>
                  <tr>
                    <td colSpan="4"><strong>Totals</strong></td>
                    <td><strong>{pageTotal.toFixed(2)}</strong></td>
                    <td></td>
                    <td>
                      <strong>
                        {invoiceData.totalAmount.toLocaleString("en-LK", {
                          minimumFractionDigits: 2,
                        })}
                      </strong>
                    </td>
                    <td></td>
                  </tr>
                </tfoot>

              </table>
              <Pagination
                current={params.page}
                pageSize={params.limit}
                total={invoiceData.total}
                onChange={(page, pageSize) => {
                  setParams({
                    page,
                    limit: pageSize,
                  });
                }}
                showSizeChanger
                pageSizeOptions={["10", "20", "50", "100"]}
              />
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Invoicereport;